import { GridColumnMenuProps, GridPreferencePanelsValue } from '@mui/x-data-grid-premium'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { MutableRefObject, ReactNode, createContext, useContext, useState } from 'react'
import { DossGridFilterItem } from '~/components/tables/filters'

export enum TableMenuTypes {
  COLUMN = 'COLUMN',
  TABLE = 'TABLE',
}

export enum TableMenuOptions {
  FILTER = 'FILTER',
  SORT = 'SORT',
  SELECT_COLUMNS = 'SELECT_COLUMNS',
  EXPORT = 'EXPORT',
}

export interface openMenuProps extends Pick<GridColumnMenuProps, 'colDef'> {
  sortDirection?: 'asc' | 'desc' | 'delete'
}

interface TableMenuContextProps {
  activeMenu: TableMenuTypes | null
  selectedOption: TableMenuOptions | null
  isOpen: boolean
  openMenu: (
    menuType: TableMenuTypes,
    option: TableMenuOptions,
    props?: openMenuProps,
    apiRef?: MutableRefObject<GridApiPremium>,
    exportTableId?: string,
    exportBacklinkColumnId?: string,
  ) => void
  closeMenu: () => void
  allowInfiniteScroll: boolean
  exportTableId: string | null
  exportBacklinkColumnId: string | null
  pendingSortField: string | null
  pendingSortDirection: 'asc' | 'desc' | 'delete' | null
  pendingFilterItem: DossGridFilterItem | null
}

export type TableMenuProviderProps = {
  children: ReactNode
  allowInfiniteScroll: boolean
  tableId?: string
}

const TableMenuContext = createContext<TableMenuContextProps | undefined>(undefined)

export const useTableMenuContext = () => {
  const context = useContext(TableMenuContext)
  if (!context) {
    throw new Error('useTableMenuContext must be used within a TableMenuProvider')
  }
  return context
}

/**
 * Provider for passing around table menu context for filter, sort, column selection, export.
 */
export const TableMenuProvider = ({
  children,
  allowInfiniteScroll,
  tableId,
}: TableMenuProviderProps) => {
  const [activeMenu, setActiveMenu] = useState<TableMenuTypes | null>(null)
  const [selectedOption, setSelectedOption] = useState<TableMenuOptions | null>(null)
  const [exportTableId, setExportTableId] = useState<string | null>(null)
  const [exportBacklinkColumnId, setExportBacklinkColumnId] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [pendingSortField, setPendingSortField] = useState<string | null>(null)
  const [pendingSortDirection, setPendingSortDirection] = useState<
    'asc' | 'desc' | 'delete' | null
  >(null)
  // Since we are extending the grid filter item to include the table id,
  // we need to store the entire item in the pending state because MUI won't know how to handle it
  const [pendingFilterItem, setPendingFilterItem] = useState<DossGridFilterItem | null>(null)

  const openMenu = (
    menuType: TableMenuTypes,
    option: TableMenuOptions,
    props?: openMenuProps,
    apiRef?: MutableRefObject<GridApiPremium>,
    exportTableId?: string,
    exportBacklinkColumnId?: string,
  ) => {
    setActiveMenu(menuType)
    setSelectedOption(option)
    setIsOpen(true)

    switch (option) {
      case TableMenuOptions.FILTER:
        if (menuType === TableMenuTypes.COLUMN && props && tableId) {
          setPendingFilterItem({
            tableId,
            field: props.colDef.field,
            operator: props.colDef.filterOperators?.[0]?.value || '',
            value: '',
          })
        }
        apiRef?.current.showFilterPanel()

        break
      case TableMenuOptions.SORT:
        if (menuType === TableMenuTypes.COLUMN && props) {
          setPendingSortField(props.colDef.field)
          setPendingSortDirection(props.sortDirection || 'asc')
        }
        // We use showFilterPanel() because both filtering and sorting use the same panel
        apiRef?.current.showFilterPanel()
        break
      case TableMenuOptions.SELECT_COLUMNS:
        apiRef?.current.showPreferences(GridPreferencePanelsValue.columns)
        break
      case TableMenuOptions.EXPORT: {
        if (!exportTableId) {
          throw new Error('Table ID is required for exporting')
        }
        menuType === TableMenuTypes.COLUMN &&
          console.error('Exporting from column menu is not supported')
        setExportTableId(exportTableId)
        setExportBacklinkColumnId(exportBacklinkColumnId ?? null)
        break
      }
    }
  }

  const closeMenu = () => {
    setIsOpen(false)
    setActiveMenu(null)
    setSelectedOption(null)
    setExportTableId(null)
    setExportBacklinkColumnId(null)
    setPendingSortField(null)
    setPendingSortDirection(null)
    setPendingFilterItem(null)
  }

  return (
    <TableMenuContext.Provider
      value={{
        activeMenu,
        selectedOption,
        isOpen,
        openMenu,
        closeMenu,
        allowInfiniteScroll,
        exportTableId,
        exportBacklinkColumnId,
        pendingSortField,
        pendingSortDirection,
        pendingFilterItem,
      }}
    >
      {children}
    </TableMenuContext.Provider>
  )
}
